/**
 * Public page for accepting a one-time invite token.
 *
 * URL: /accept-invite?token=<base64url>
 *
 * Flow:
 *  1. Load the invite metadata to show who invited and what role.
 *  2. User picks accountName + password.
 *  3. POST /api/auth/invite/accept → server creates the user, auto-logs in,
 *     and we navigate to /dashboard.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Center,
  Group,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { authApi, type InviteLookup } from '../api/auth';
import { ApiCallError } from '../api/client';
import { useTranslation } from '../i18n/LocaleContext';

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [lookup, setLookup] = useState<InviteLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLookupError(t('acceptInvite.tokenMissing'));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await authApi.lookupInvite(token);
        if (!cancelled) setLookup(data);
      } catch (err) {
        if (cancelled) return;
        setLookupError(
          err instanceof ApiCallError
            ? `${err.code}: ${err.message}`
            : t('acceptInvite.invalidOrExpired'),
        );
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const form = useForm({
    initialValues: { accountName: '', password: '', confirm: '' },
    validate: {
      accountName: (v) => (v.trim().length === 0 ? t('acceptInvite.valRequired') : null),
      password: (v) => (v.length < 12 ? t('acceptInvite.valMinChars') : null),
      confirm: (v, vals) => (v !== vals.password ? t('acceptInvite.valMismatch') : null),
    },
  });
  const mutation = useMutation({
    meta: {
      successKey: 'notify.invite.accept.ok',
      errorKey: 'notify.invite.accept.fail',
    },
    mutationFn: () =>
      authApi.acceptInvite({
        token,
        password: form.values.password,
        accountName: form.values.accountName.trim(),
      }),
    onSuccess: () => navigate('/dashboard', { replace: true }),
  });

  return (
    <Center mih="100vh" p="md">
      <Card w={460} shadow="md" radius="md" withBorder>
        <Stack gap="md">
          <Title order={3} ta="center">{t('acceptInvite.title')}</Title>

          {lookupError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>{lookupError}</Alert>
          )}

          {!lookup && !lookupError && (
            <Center py="xl"><Loader /></Center>
          )}

          {lookup && (
            <>
              <Card withBorder bg="gray.0">
                <Stack gap={4}>
                  <Text size="sm" c="dimmed">{t('acceptInvite.invitedAs')}</Text>
                  <Group gap={6}>
                    <Text fw={600}>{lookup.roleName ?? t('acceptInvite.noRole')}</Text>
                    {lookup.merchantName && (
                      <Text size="sm" c="dimmed">@ {lookup.merchantName}</Text>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {t('acceptInvite.invitedByExpires', {
                      invitedBy: lookup.invitedBy,
                      expiresAt: new Date(lookup.expiresAt).toLocaleString(),
                    })}
                  </Text>
                </Stack>
              </Card>

              <form onSubmit={form.onSubmit(() => mutation.mutate())}>
                <Stack gap="sm">
                  <TextInput
                    label={t('acceptInvite.emailLabel')}
                    value={lookup.email}
                    disabled
                  />
                  <TextInput
                    label={t('acceptInvite.nameLabel')}
                    description={t('acceptInvite.nameDesc')}
                    required
                    {...form.getInputProps('accountName')}
                  />
                  <PasswordInput
                    label={t('acceptInvite.passwordLabel')}
                    description={t('acceptInvite.passwordDesc')}
                    required
                    {...form.getInputProps('password')}
                  />
                  <PasswordInput
                    label={t('acceptInvite.confirmLabel')}
                    required
                    {...form.getInputProps('confirm')}
                  />
                  <Button type="submit" loading={mutation.isPending} fullWidth>
                    {t('acceptInvite.submit')}
                  </Button>
                </Stack>
              </form>
            </>
          )}

          <Text ta="center" size="xs" c="dimmed">
            {t('acceptInvite.haveAccount')} <Anchor href="/login">{t('acceptInvite.signIn')}</Anchor>
          </Text>
        </Stack>
      </Card>
    </Center>
  );
}
